import { ProcessTaskStatus, ProcessTaskType } from '@common/enums';

export enum ProcessTaskStatusRelation {
    'performer',
    'requester',
    'admin',
}

export function getProcessTaskStatusLabel(
    type: ProcessTaskType,
    relation: ProcessTaskStatusRelation,
    status?: ProcessTaskStatus
): string {
    return `${processTaskStatusLabels[type][relation]}${status === ProcessTaskStatus.inProgress ? ' (Concept)' : ''}`;
}

// Every status needs three labels: for the performer, for the requester, and for an admin
type Labels = [string, string, string];

// These are used twice
const dietitianReportLabels: Labels = ['Gespreksverslag', 'In behandeling', 'Gespreksverslag diëtist'];
const lifestyleCoachReportLabels: Labels = ['Gespreksverslag', 'In behandeling', 'Gespreksverslag lifestylecoach'];
const nurseSpecialistReportLabels: Labels = [
    'Gespreksverslag',
    'In behandeling',
    'Gespreksverslag verpleegkundig specialist',
];
const psychologistReportLabels: Labels = ['Gespreksverslag', 'In behandeling', 'Gespreksverslag psycholoog'];

const processTaskStatusLabels: { [Type in ProcessTaskType]: Labels } = {
    [ProcessTaskType.createQuestionnaire]: ['Vragenlijst psycholoog', 'In behandeling', 'Vragenlijst psycholoog'],
    [ProcessTaskType.createScreening]: [
        'Verslag psychologische screening',
        'In behandeling',
        'Verslag psychologische screening',
    ],
    [ProcessTaskType.dietConsultReport]: [
        'Verslag post-operatief diëtistisch consult',
        'In behandeling',
        'Verslag post-operatief diëtistisch consult',
    ],
    [ProcessTaskType.dietitianReport]: dietitianReportLabels,
    [ProcessTaskType.finishDraftAddToMdo]: ['Aanmelden MDO', 'In behandeling', 'Aanmelden MDO'],
    [ProcessTaskType.finishDraftCloseProcess]: ['Afbreken voortraject', 'In behandeling', 'Afbreken voortraject'],
    [ProcessTaskType.finishDraftConsult]: ['Consult', 'Consult', 'Consult'],
    [ProcessTaskType.finishDraftDietitianReport]: dietitianReportLabels,
    [ProcessTaskType.finishDraftEvaluation]: ['Evaluatie', 'Evaluatie', 'Evaluatie'],
    [ProcessTaskType.finishDraftFollowUpConsult]: ['Vervolgconsult', 'Vervolgconsult', 'Vervolgconsult'],
    [ProcessTaskType.finishDraftHeadacheReferral]: [
        'Aanvraag hoofdpijnonderzoek',
        'In behandeling',
        'Aanvraag hoofdpijnonderzoek',
    ],
    [ProcessTaskType.finishDraftHearingLossExamination]: [
        'Audiologisch onderzoek',
        'In behandeling',
        'Audiologisch onderzoek',
    ],
    [ProcessTaskType.finishDraftLifestyleCoachReport]: lifestyleCoachReportLabels,
    [ProcessTaskType.finishDraftLumbarRadiculopathyReferral]: [
        'Aanvraag consult',
        'In behandeling',
        'Aanvraag consult',
    ],
    [ProcessTaskType.finishDraftMedConsultExamResults]: [
        'Uitslagen onderzoek',
        'Uitslagen onderzoek',
        'Uitslagen onderzoek',
    ],
    [ProcessTaskType.finishDraftNote]: ['Notitie/bijlage', 'In behandeling', 'Notitie/bijlage'],
    [ProcessTaskType.finishDraftNurseSpecialistReport]: nurseSpecialistReportLabels,
    [ProcessTaskType.finishDraftEchographyConsultRequest]: ['Aanvraag consult', 'In behandeling', 'Aanvraag consult'],
    [ProcessTaskType.finishDraftGrading]: ['Beoordeling', 'In behandeling', 'Beoordeling'],
    [ProcessTaskType.finishDraftPoliReport]: ['Gespreksverslag', 'In behandeling', 'Gespreksverslag polikliniek'],
    [ProcessTaskType.finishDraftPsychologistReport]: psychologistReportLabels,
    [ProcessTaskType.finishDraftRequest]: ['Aanvraag', 'In behandeling', 'Aanvraag'],
    [ProcessTaskType.finishDraftReferral]: ['Verwijzing', 'Verwijzing', 'Verwijzing'],
    [ProcessTaskType.finishDraftSecretarialAssistentFollowUp]: [
        'Verslag follow-up',
        'In behandeling',
        'Verslag follow-up (secretariaat)',
    ],
    [ProcessTaskType.finishDraftTreatmentChoice]: ['Behandelkeuze', 'Behandelkeuze', 'Behandelkeuze'],
    [ProcessTaskType.lifestyleCoachReport]: lifestyleCoachReportLabels,
    [ProcessTaskType.medConsultPlanAppointment]: [
        'Afspraak medische screening',
        'In behandeling',
        'Afspraak medische screening',
    ],
    [ProcessTaskType.nurseSpecialistFollowUp]: [
        'Verslag follow-up',
        'In behandeling',
        'Verslag follow-up verpleegkundig specialist',
    ],
    [ProcessTaskType.nurseSpecialistMedScreeningReport]: [
        'Verslag medische screening',
        'In behandeling',
        'Verslag medische screening',
    ],
    [ProcessTaskType.nurseSpecialistReferral]: ['Verwijsbrief uploaden', 'In behandeling', 'Verwijsbrief uploaden'],
    [ProcessTaskType.nurseSpecialistReport]: nurseSpecialistReportLabels,
    [ProcessTaskType.performAddGeneralPractitioner]: ['Huisarts toevoegen', 'In behandeling', 'Huisarts toevoegen'],
    [ProcessTaskType.performAddSkinPrickTestResults]: ['Vul huidpriktest in', 'In behandeling', 'Vul huidpriktest in'],
    [ProcessTaskType.performCaroInactivityReport]: [
        'Verslag app-inactiviteit',
        'In behandeling',
        'Verslag app-inactiviteit',
    ],
    [ProcessTaskType.performConsultation]: ['Uit te voeren', 'In behandeling', 'Bij specialist'],
    [ProcessTaskType.performDietPlanAppointment]: ['Afspraak diëtist', 'In behandeling', 'Afspraak diëtist'],
    [ProcessTaskType.performDietScreening]: [
        'Verslag diëtistische screening',
        'In behandeling',
        'Verslag diëtistische screening',
    ],
    [ProcessTaskType.performEnrollment]: ['Aanmelden natraject', 'In behandeling', 'Aanmelden natraject'],
    [ProcessTaskType.performGrading]: ['Te beoordelen', 'In behandeling', 'Bij grader'],
    [ProcessTaskType.performHeadacheAssessment]: [
        'Beoordeling hoofdpijnonderzoek',
        'In behandeling',
        'Beoordeling hoofdpijnonderzoek',
    ],
    [ProcessTaskType.performHeadacheExamination]: [
        'Verslag hoofdpijnonderzoek',
        'In behandeling',
        'Verslag hoofdpijnonderzoek',
    ],
    [ProcessTaskType.performHearingLossAssessment]: [
        'Beoordeling audiologisch onderzoek',
        'In behandeling',
        'Beoordeling audiologisch onderzoek',
    ],
    [ProcessTaskType.performHearingLossAudiologistConsult]: [
        'Audiologisch consult',
        'In behandeling',
        'Audiologisch consult',
    ],
    [ProcessTaskType.performHearingLossAudiologistFollowUpConsult]: [
        'Audiologisch vervolgconsult',
        'In behandeling',
        'Audiologisch vervolgconsult',
    ],
    [ProcessTaskType.performLumbarRadiculopathyAssessment]: ['Beoordeling', 'In behandeling', 'Beoordeling'],
    [ProcessTaskType.performLumbarRadiculopathyExamination]: ['Onderzoek', 'In behandeling', 'Onderzoek'],
    [ProcessTaskType.performMdoInterimReportStep]: ['Verslag MDO', 'In behandeling', 'Verslag MDO (tussentijds)'],
    [ProcessTaskType.performMdoReportStep]: ['Verslag MDO', 'In behandeling', 'Verslag MDO'],
    [ProcessTaskType.performMedConsultAssessment]: ['Totaaloordeel', 'In behandeling', 'Totaaloordeel'],
    [ProcessTaskType.performProcedure]: ['Uit te voeren', 'In behandeling', 'Bij biometrist'],
    [ProcessTaskType.performEchographyConsultResponse]: ['Antwoord consult', 'In behandeling', 'Bij radioloog'],
    [ProcessTaskType.performEchographyAdditionalReply]: ['Reactie consult', 'In behandeling', 'Bij radioloog'],
    [ProcessTaskType.performEchographyAdditionalQuestionRequest]: ['Reactie consult', 'In behandeling', 'Bij huisarts'],
    [ProcessTaskType.performQualityControl]: ['Te controleren', 'In behandeling', 'Bij controleur'],
    [ProcessTaskType.performResumeMdoInterimStep]: [
        'Hervatten voortraject',
        'In behandeling',
        'Hervatten voortraject (tussentijds)',
    ],
    [ProcessTaskType.performSecondRoundAnswer]: ['Verstuurd (2e ronde)', 'In behandeling', 'Beantwoord (2e ronde)'],
    [ProcessTaskType.performResumeMdoStep]: ['Hervatten voortraject', 'In behandeling', 'Hervatten voortraject'],
    [ProcessTaskType.performSurgeryDate]: ['OK datum doorgeven', 'In behandeling', 'OK datum doorgeven'],
    [ProcessTaskType.psychologistReport]: psychologistReportLabels,
    [ProcessTaskType.secretarialAssistentMedScreeningReport]: [
        'Verslag medische screening',
        'In behandeling',
        'Verslag medische screening (secr.)',
    ],
    [ProcessTaskType.treatmentReport]: ['Verslag behandeling', 'In behandeling', 'Verslag behandeling'],
    [ProcessTaskType.configureHolter]: ['Holter instellen', 'In behandeling', 'Holter instellen'],
    [ProcessTaskType.uploadHolterDiary]: ['Holter dagboek uploaden', 'In behandeling', 'Bij analist'],
};
